import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Download, AlertCircle, Loader2 } from 'lucide-react';

interface LibreOfficeOnlineViewerProps {
  documentUrl: string;
  fileName?: string;
  className?: string;
  height?: string;
}

export const LibreOfficeOnlineViewer: React.FC<LibreOfficeOnlineViewerProps> = ({
  documentUrl,
  fileName = 'Document',
  className = '',
  height = '600px'
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Force refresh when document URL changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setRefreshKey(prev => prev + 1);
  }, [documentUrl]);

  // LibreOffice Online embed URL (you would need to set up your own instance)
  // For demo purposes, this falls back to a public LibreOffice Online demo
  const cacheBuster = new Date().getTime();
  const embedUrl = `https://demo.collaboraoffice.com/loleaflet/dist/loleaflet.html?file_path=${encodeURIComponent(documentUrl)}&t=${cacheBuster}`;

  const iframeKey = `libre-${documentUrl}-${cacheBuster}-${refreshKey}`;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openInNewTab = () => {
    window.open(embedUrl, '_blank');
  };

  const handleIframeLoad = () => {
    setIsLoading(false);
    console.log('LibreOffice document loaded successfully:', documentUrl);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
    console.error('Failed to load LibreOffice document:', documentUrl);
  };

  return (
    <div className={`${className} h-full`}>
      <div className="h-full flex flex-col">
        {/* Header with controls */}
        <div className="flex justify-between items-center p-2 border-b bg-muted/20">
          <span className="text-sm font-medium truncate">{fileName}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={openInNewTab}>
              <ExternalLink className="w-3 h-3 mr-1" />
              Open
            </Button>
            <Button size="sm" variant="outline" onClick={handleDownload}>
              <Download className="w-3 h-3 mr-1" />
              Download
            </Button>
          </div>
        </div>

        {/* Document viewer */}
        <div className="relative w-full flex-1 bg-gray-100" style={{ height }}>
          {/* Loading state */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
                <p className="text-sm text-muted-foreground">Loading LibreOffice Online...</p>
              </div>
            </div>
          )}

          {/* Error state */}
          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
              <div className="text-center p-6 max-w-md">
                <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
                <h3 className="font-semibold mb-2">LibreOffice Viewer Failed</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  The document could not be displayed with LibreOffice Online. This might be because:
                </p>
                <ul className="text-xs text-muted-foreground text-left mb-4 space-y-1">
                  <li>• LibreOffice Online service is not configured</li>
                  <li>• The document URL is not accessible</li>
                  <li>• The file format is not supported</li>
                  <li>• Network connectivity issues</li>
                </ul>
                <div className="flex gap-2 justify-center">
                  <Button size="sm" variant="outline" onClick={handleDownload}>
                    Download File
                  </Button>
                </div>
              </div>
            </div>
          )}

          <iframe
            key={iframeKey}
            src={embedUrl}
            width="100%"
            height="100%"
            frameBorder="0"
            title={`LibreOffice Viewer - ${fileName}`}
            className="w-full h-full border-0"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
            onLoad={handleIframeLoad}
            onError={handleIframeError}
          />
        </div>
      </div>
    </div>
  );
};